// Találkozó create/edit — jobb oldali beúszó FormSheet (a nem-clients/accounts modulok szerkesztő-mintája).
// Egy komponens KÉT módban: `meeting == null` → LÉTREHOZÁS (POST), különben SZERKESZTÉS (PATCH If-Match/OCC).
// Önálló: saját react-hook-form + create/update mutáció (siker → lista-invalidálás + zárás; 422 → mező-hiba;
// 409 → OCC-toast; 403 → jog-toast). Az üzleti invariánsokat (end>start, nem-múltbeli, online-link-exkluzivitás)
// a BFF is kényszeríti (422) — a form ELŐ-validál (gyors visszajelzés), a szerver a forrás-igazság.
//
// IDŐ: az Odin UTC-ben tárol; a UI a böngésző HELYI idejét mutatja (datetime-local). A konverzió (UTC↔helyi) a
// betöltés/submit határán történik (isoToLocalInput / localInputToIso). RÉSZTVEVŐK + RSVP + meghívó KÜLÖN (e-mail-fázis).
import { type MeetingRowSchema } from '@atlas/contracts';
import { MEETING_LOCATION_KEYS } from '@atlas/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { buildMeetingErrorMap } from '@/features/meetings/form';

import { MeetingParticipantConflictList } from '@/components/meetings/meeting-participant-conflict-list';
import { MeetingRecurrenceFields } from '@/components/meetings/meeting-recurrence-fields';
import {
  type MeetingSeriesScope,
  MeetingSeriesScopeOptions,
} from '@/components/meetings/meeting-series-scope-options';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { EntityMultiSelect, type MultiSelectGroup } from '@/components/shared/entity-multiselect';
import {
  DateTimeField,
  SelectField,
  TextareaField,
  TextField,
} from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { meetingKeys } from '@/features/meetings/api';
import {
  BUSINESS_END_HOUR,
  BUSINESS_START_HOUR,
  defaultEndFor,
  defaultStartForNow,
  isWithinBusinessHours,
} from '@/features/meetings/business-hours';
import {
  employeesToCheck,
  fetchParticipantConflicts,
  type MeetingParticipantConflict,
} from '@/features/meetings/participant-conflicts';
import {
  describeSeries,
  previewRecurrence,
  RECURRENCE_DEFAULTS,
  recurrenceRuleOf,
} from '@/features/meetings/recurrence';
import { api } from '@/lib/api';
import { DEFAULT_TIME_ZONE } from '@/lib/format-datetime';
import { toPersonOptions, toPersonOptionsWith } from '@/lib/person-options';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type MeetingRow = z.infer<typeof MeetingRowSchema>;

// A helyszín-kulcsok a KÖZÖS listából (a szűrő, a naptár-szín és a levél-címke ugyanezt használja); címkék az i18n-ből.
const LOCATION_OPTIONS = MEETING_LOCATION_KEYS;
const ZOOM_ACCOUNT_OPTIONS = ['zoom_account_1', 'zoom_account_2'];
const NONE = '__none__';

/**
 * ⚠️ A DÁTUM-KONVERZIÓ A MEGJELENÍTÉSI ZÓNÁBAN (Europe/Budapest) MEGY, NEM A BÖNGÉSZŐ ZÓNÁJÁBAN.
 *
 * MIÉRT: az app szabálya „UTC-ben tárolva, `Europe/Budapest`-ben megjelenítve" — a lista-oszlopok és a naptár
 * is FIX zónában formáznak (`format-datetime.ts`, `calendar-map.ts`). Ha viszont a szerkesztő-form a
 * böngésző zónáját használja (`new Date(local)`, `getHours()`), akkor egy Budapesten KÍVÜLI gépen ugyanaz a
 * meeting MÁS időpontot mutat a naptáron és a formban — és a mentés is elcsúszik. (Ez a hiba-osztály
 * élesben is jelentkezett: a naptár 07:00-t, a form 09:00-t írt ugyanarra a sorra.)
 */
const FORM_TIME_ZONE = DEFAULT_TIME_ZONE;

/** Az idő-választó rácsának ablaka `HH:mm`-ben — a munkaidő-konstansokból (egyetlen igazság-forrás). */
const BUSINESS_WINDOW = {
  from: `${String(BUSINESS_START_HOUR).padStart(2, '0')}:00`,
  to: `${String(BUSINESS_END_HOUR).padStart(2, '0')}:00`,
} as const;

/** ISO (UTC) → 'YYYY-MM-DDTHH:mm' a MEGJELENÍTÉSI zónában (a datetime-local inputhoz). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatInTimeZone(d, FORM_TIME_ZONE, "yyyy-MM-dd'T'HH:mm");
}
/** 'YYYY-MM-DDTHH:mm' (a MEGJELENÍTÉSI zóna fal-ideje) → ISO (UTC). Üres → ''. */
function localInputToIso(local: string): string {
  if (!local) return '';
  const d = fromZonedTime(local, FORM_TIME_ZONE);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}
/**
 * A mező-SHAPE (üzenetek nélkül) — a cross-field szabályok a KOMPONENSBEN kapnak i18n-üzenetet (`formSchema`),
 * mert a modul-szinten nincs `t`. Korábban itt HARDCODE-olt magyar üzenetek álltak, amik angol felületen is
 * magyarul jelentek meg.
 */
const baseSchema = z.object({
  title: z.string().trim().min(1).max(255),
  description: z.string().max(255),
  locationKey: z.string().min(1),
  startsAt: z.string().min(1),
  endsAt: z.string().min(1),
  zoomAccountKey: z.string(),
  zoomLink: z.string().max(2083),
  googleMeetLink: z.string().max(2083),
  microsoftTeamsLink: z.string().max(2083),
  // Résztvevők: belső munkatársak (type=internal) + kapcsolattartók (type=partner) — a BFF pivot-diffeli.
  employeeIds: z.array(z.string()),
  contactIds: z.array(z.string()),
  // ISMÉTLŐDÉS — csak létrehozáskor (lásd `meeting-recurrence-fields.tsx`)
  recurrenceFrequency: z.enum(['none', 'weekly', 'biweekly', 'monthly', 'workdays']),
  recurrenceWeekdays: z.array(z.string()),
  recurrenceEnd: z.enum(['count', 'until']),
  recurrenceCount: z.string(),
  recurrenceUntil: z.string(),
});
type FormValues = z.infer<typeof baseSchema>;

const EMPTY: FormValues = {
  title: '',
  description: '',
  locationKey: 'meeting_room',
  startsAt: '',
  endsAt: '',
  zoomAccountKey: NONE,
  zoomLink: '',
  googleMeetLink: '',
  microsoftTeamsLink: '',
  employeeIds: [],
  contactIds: [],
  ...RECURRENCE_DEFAULTS,
};

export interface MeetingFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő találkozó (hordozza az OCC `version`-t + a linkeket). */
  meeting: MeetingRow | null;
  /** CREATE-módban előtöltött HELYI kezdő-időpont ('YYYY-MM-DDTHH:mm') — a naptár üres-idősáv kattintásából. */
  initialStart?: string | undefined;
  /** Van-e törlési jog (`meetings.delete`) — ekkor a szerkesztő láb-gombja felkínálja a törlést. */
  canDelete?: boolean;
  /** A törlés kérése (a megerősítést/mutációt a szülő intézi) — CSAK szerkesztésnél + `canDelete` mellett. */
  onRequestDelete?: (meeting: MeetingRow) => void;
}

export function MeetingFormSheet({
  open,
  onOpenChange,
  meeting,
  initialStart,
  canDelete = false,
  onRequestDelete,
}: MeetingFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEdit = meeting !== null;

  // Résztvevő-picker opció-listák (belső munkatársak + kapcsolattartók) — csak a szerkesztő megnyitásakor.
  const refsQ = api.meetingRefs.useQuery(
    ['meeting-refs'],
    {},
    { queryKey: ['meeting-refs'], enabled: open },
  );
  // A név ALAKJA és a lista SORRENDJE nyelv-függő (hu: vezetéknév elöl; az ABC-rend a megjelenített
  // alakot követi) — a közös `toPersonOptions` formáz és rendez, a BFF csak stabil alap-sorrendet ad.
  //
  // ⚠️ A `useMemo` LOAD-BEARING, nem optimalizálás: a `toPersonOptions` minden hívásnál ÚJ tömböt ad, és ez a
  // tömb a `EntityMultiSelect` (Radix Popover + checkbox-lista) propja. Egy résztvevő kijelölése
  // `setValue`-t hív → újrarender → új tömb-identitás → a popover-tartalom remountol → a kijelölés-effekt
  // újra fut… → „Maximum update depth exceeded" (élesben is jelentkezett). A memo a
  // FORRÁS-adathoz és a NYELVHEZ kötve stabil identitást ad.
  const refsBody = refsQ.data?.status === 200 ? refsQ.data.body : null;
  const employeeOptions = useMemo(
    () => (refsBody ? toPersonOptions(refsBody.employees, i18n.language) : []),
    [refsBody, i18n.language],
  );
  // a kapcsolattartó HOTELNEVEI másodlagos névként: „Név (Hotel +N)", és a hotelnévre is lehet keresni
  const contactOptions = useMemo(
    () =>
      refsBody
        ? toPersonOptionsWith(refsBody.contacts, i18n.language).map((c) => ({
            id: c.id,
            name: c.name,
            secondary: c.accountNames,
          }))
        : [],
    [refsBody, i18n.language],
  );
  // A munkatárs-választó CSOPORT-fája (részleg → csapat): a fejléc egy kattintással a csoport összes tagját
  // kijelöli — a mentés továbbra is egyedi munkatárs-id-ket kap. Ugyanazért memo, mint fent (stabil identitás).
  const employeeGroups = useMemo<MultiSelectGroup[]>(
    () =>
      (refsBody?.employeeGroups ?? []).map((d) => ({
        id: d.id,
        name: d.name,
        optionIds: d.memberIds,
        children: d.teams.map((tm) => ({ id: tm.id, name: tm.name, optionIds: tm.memberIds })),
      })),
    [refsBody],
  );

  /**
   * A cross-field szabályok i18n-üzenettel: vég > kezdés · legfeljebb egy online-link · MUNKAIDŐ-ABLAK
   * (06:00–22:00). Az ablak ugyanabból a konstans-párból jön, amit a naptár rács-sávjai és az előtöltés is
   * használ — így a felület nem ajánl fel olyan időpontot, amit a mentés visszadob.
   */
  const formSchema = useMemo(
    () =>
      baseSchema
        .refine((v) => localInputToIso(v.endsAt) > localInputToIso(v.startsAt), {
          path: ['endsAt'],
          message: t('validation:meetings.endBeforeStart'),
        })
        .refine((v) => isWithinBusinessHours(v.startsAt), {
          path: ['startsAt'],
          message: t('validation:meetings.outsideBusinessHours', BUSINESS_WINDOW),
        })
        .refine((v) => isWithinBusinessHours(v.endsAt), {
          path: ['endsAt'],
          message: t('validation:meetings.outsideBusinessHours', BUSINESS_WINDOW),
        })
        .refine(
          (v) => [v.zoomLink, v.googleMeetLink, v.microsoftTeamsLink].filter(Boolean).length <= 1,
          { path: ['zoomLink'], message: t('validation:meetings.oneOnlineLink') },
        )
        // ISMÉTLŐDÉS elő-validálása ugyanazzal a kibontással, amit a BFF ír (a szerver ettől függetlenül 422-t ad)
        .superRefine((v, ctx) => {
          const rule = recurrenceRuleOf(v);
          if (rule === null) return;
          const r = previewRecurrence(localInputToIso(v.startsAt), localInputToIso(v.endsAt), rule);
          if (!r.ok) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['recurrenceFrequency'],
              message: t(`validation:meetings.recurrence.${r.error}`),
            });
          }
        }),
    [t],
  );

  // A lokalizált hibatérkép a feature `form.ts`-éből (a repo mintája) — enélkül a zod nyers, ANGOL
  // alapüzenete jelent meg a magyar felületen a kötelező mezőkön.
  const errorMap = useMemo(() => buildMeetingErrorMap(t), [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema, { errorMap }) as Resolver<FormValues>,
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY,
  });
  const { reset, handleSubmit, setError, watch, setValue, getValues } = form;
  // az előző kezdés (a vége-követéshez) — a reset és minden kezdés-változás frissíti
  const prevStartRef = useRef('');

  // MINDEN MEGNYITÁSKOR alaphelyzet (az `open` is függőség): az „Új megbeszélés" gomb mindig `meeting: null`-lal
  // nyit, és ha a reset csak a `meeting` változására futott, a mentett (vagy bezárt) űrlap tartalma ott maradt —
  // az újonnan beírt cím a régi mögé fűződött (kolléga-észrevétel). Zárt sheeten nem nullázunk (a záró animáció
  // alatt ne villanjon üresre).
  useEffect(() => {
    if (!open) return;
    if (meeting) {
      reset({
        title: meeting.title,
        description: meeting.description ?? '',
        locationKey: meeting.locationKey ?? 'meeting_room',
        startsAt: isoToLocalInput(meeting.startsAt),
        endsAt: isoToLocalInput(meeting.endsAt),
        zoomAccountKey: meeting.zoomAccountKey ?? NONE,
        zoomLink: meeting.zoomLink ?? '',
        googleMeetLink: meeting.googleMeetLink ?? '',
        microsoftTeamsLink: meeting.microsoftTeamsLink ?? '',
        // A résztvevőket a `participantRole` szerint két listára bontjuk (a picker type-szűrt párja).
        employeeIds: meeting.participants
          .filter((p) => p.participantRole !== 'partner')
          .map((p) => p.id),
        contactIds: meeting.participants
          .filter((p) => p.participantRole === 'partner')
          .map((p) => p.id),
        // a sorozat szabálya szerkesztéskor nem módosítható — a mezők rejtettek, az értékük az alap
        ...RECURRENCE_DEFAULTS,
      });
    } else {
      // CREATE: a kezdés SOSEM üres/éjfél — a naptár-slotból jött időpont, slot nélkül (az „Új megbeszélés"
      // gombról) a MOSTHOZ igazított alap (reggel 08:00 · napközben a következő egész óra · este holnap 08:00);
      // a vég alapból +1 óra, a záró óráig szorítva. MIÉRT: a datetime-local üresen indulva 00:00-t ajánlott fel,
      // a vak „ma 08:00" pedig este múltbeli időpontot.
      const now = new Date();
      const nowMinutes =
        Number(formatInTimeZone(now, FORM_TIME_ZONE, 'H')) * 60 +
        Number(formatInTimeZone(now, FORM_TIME_ZONE, 'm'));
      const start =
        initialStart ??
        defaultStartForNow(formatInTimeZone(now, FORM_TIME_ZONE, 'yyyy-MM-dd'), nowMinutes);
      reset({ ...EMPTY, startsAt: start, endsAt: defaultEndFor(start) });
    }
    prevStartRef.current = getValues('startsAt');
  }, [open, meeting, initialStart, reset, getValues]);

  // A VÉGE KÖVETI A KEZDÉST (kolléga-észrevétel; a Google Naptár / Outlook mintája): a kezdés kézi módosításakor
  // (dátum vagy idő) a vége UGYANANNYIVAL tolódik — az időtartam marad, akár az alap 1 óra, akár egy kézzel
  // beállított hossz. Az eltolás valós (zónázott) időben számol, így nap- és óraátállítás-határon is pontos. A
  // reset (megnyitás) nem tol: csak a felhasználói `change` számít, és a reset az előző kezdést is átírja.
  useEffect(() => {
    const sub = watch((values, { name, type }) => {
      if (name !== 'startsAt') return;
      const prev = prevStartRef.current;
      const next = values.startsAt ?? '';
      prevStartRef.current = next;
      if (type !== 'change' || prev === '' || next === '' || !values.endsAt) return;
      const delta = Date.parse(localInputToIso(next)) - Date.parse(localInputToIso(prev));
      const end = Date.parse(localInputToIso(values.endsAt));
      if (!Number.isFinite(delta) || delta === 0 || !Number.isFinite(end)) return;
      setValue('endsAt', isoToLocalInput(new Date(end + delta).toISOString()), {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
    return () => sub.unsubscribe();
  }, [watch, setValue]);

  // a sorozat-alkalom mentésének hatóköre (a hatókör-dialógus választása; egyszerinél mindig `this`)
  const scopeRef = useRef<MeetingSeriesScope>('this');

  const mutation = useEntityMutation<FormValues>({
    mutationFn: (values) => {
      const rule = isEdit ? null : recurrenceRuleOf(values);
      const body = {
        title: values.title,
        description: values.description === '' ? null : values.description,
        locationKey: values.locationKey,
        startsAt: localInputToIso(values.startsAt),
        endsAt: localInputToIso(values.endsAt),
        zoomAccountKey: values.zoomAccountKey === NONE ? null : values.zoomAccountKey,
        zoomLink: values.zoomLink === '' ? null : values.zoomLink,
        googleMeetLink: values.googleMeetLink === '' ? null : values.googleMeetLink,
        microsoftTeamsLink: values.microsoftTeamsLink === '' ? null : values.microsoftTeamsLink,
        employeeIds: values.employeeIds,
        contactIds: values.contactIds,
        ...(rule ? { recurrence: { ...rule, weekdays: [...rule.weekdays] } } : {}),
      };
      return isEdit
        ? api.meetingUpdate.mutation({
            params: { id: meeting.id },
            query: { scope: scopeRef.current },
            body,
            headers: { 'if-match': String(meeting.version) },
          })
        : api.meetingCreate.mutation({ body });
    },
    invalidateKeys: [meetingKeys.lists()],
    onFieldErrors: (field, messages) => {
      // A BFF i18n-KULCSOT (pl. `validation:meetings.locationConflict`) VAGY kész szöveget adhat; a kulcsot
      // itt oldjuk fel a mezőhöz (locationKey / zoomAccountKey → a megfelelő mezőn jelenik meg). Ismeretlen
      // szöveg → változatlanul (defaultValue), így a nem-kulcs mező-hibák is helyesen jelennek meg.
      // a sorozat-szabály hibája (`recurrence`) a gyakoriság-mező alá kerül
      const target = field === 'recurrence' ? 'recurrenceFrequency' : field;
      if (target in EMPTY) {
        const message = messages.map((m) => t(m, { defaultValue: m })).join(' ');
        setError(target as keyof FormValues, { message });
      }
    },
    messages: {
      success: isEdit ? t('toast:meetings.updated') : t('toast:meetings.created'),
      conflict: t('toast:meetings.conflict'),
      forbidden: t('toast:meetings.forbidden'),
      validation: t('toast:meetings.validation'),
      error: t('toast:meetings.error'),
    },
  });

  // RÉSZTVEVŐ-ÜTKÖZÉS (figyelmeztetés, nem tiltás): mentés ELŐTT lekérjük, kik szerepelnek már egy átfedő
  // megbeszélésen; ha van ilyen, sárga megerősítő dialógus — „Mentés mégis" ment, „Vissza" a formra. A lekérés
  // best-effort (hiba → nincs figyelmeztetés, a mentés megy).
  const [conflicts, setConflicts] = useState<MeetingParticipantConflict[]>([]);
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);
  const [checking, setChecking] = useState(false);
  // szinkron őr a dupla beküldés ellen: az Enter a gomb tiltásától függetlenül is submitol, és a state-frissítés
  // a következő renderig nem látszik
  const checkingRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;

  // a sheet bezárásakor a függő figyelmeztetés is elenged — különben egy bezárt űrlap mentése nyílna meg
  useEffect(() => {
    if (!open) {
      setPendingValues(null);
      setConflicts([]);
    }
  }, [open]);

  // SOROZAT-alkalom mentése ELŐTT: mire vonatkozzon (csak ez / ez és a következők / mind)
  const [scopeValues, setScopeValues] = useState<FormValues | null>(null);
  const [scopeChoice, setScopeChoice] = useState<MeetingSeriesScope>('this');
  useEffect(() => {
    if (!open) setScopeValues(null);
  }, [open]);

  const doSubmit = (values: FormValues): void => {
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  };

  const onSubmit = handleSubmit((values) => {
    if (checkingRef.current || mutation.isPending) return;
    if (isEdit && meeting.seriesId) {
      setScopeChoice('this');
      setScopeValues(values);
      return;
    }
    scopeRef.current = 'this';
    void checkAndSave(values);
  });

  const checkAndSave = async (values: FormValues): Promise<void> => {
    if (checkingRef.current || mutation.isPending) return;
    const next = {
      startsAt: localInputToIso(values.startsAt),
      endsAt: localInputToIso(values.endsAt),
      employeeIds: values.employeeIds,
    };
    const original = meeting
      ? {
          startsAt: meeting.startsAt,
          endsAt: meeting.endsAt,
          employeeIds: meeting.participants
            .filter((p) => p.participantRole !== 'partner')
            .map((p) => p.id),
          declinedIds: meeting.participants.filter((p) => p.status === 'declined').map((p) => p.id),
        }
      : null;
    const toCheck = employeesToCheck(next, original);
    if (toCheck.length > 0) {
      checkingRef.current = true;
      setChecking(true);
      let found: MeetingParticipantConflict[];
      try {
        found = await fetchParticipantConflicts({
          startsAt: next.startsAt,
          endsAt: next.endsAt,
          employeeIds: toCheck,
          excludeId: meeting?.id,
          // új sorozatnál a szabály minden alkalmára ellenőriz
          recurrence: isEdit ? null : recurrenceRuleOf(values),
        });
      } finally {
        checkingRef.current = false;
        setChecking(false);
      }
      // a lekérés alatt bezárt sheet nem nyit figyelmeztetést és nem ment
      if (!openRef.current) return;
      if (found.length > 0) {
        setConflicts(found);
        setPendingValues(values);
        return;
      }
    }
    doSubmit(values);
  };

  const locationOptions = LOCATION_OPTIONS.map((v) => ({
    value: v,
    label: t(`meetings.locationLabel.${v}`, { defaultValue: v }),
  }));
  const zoomAccountOptions = [
    { value: NONE, label: t('meetings.fields.zoomAccountNone') },
    ...ZOOM_ACCOUNT_OPTIONS.map((v) => ({
      value: v,
      label: t(`meetings.zoomAccountLabel.${v}`, { defaultValue: v }),
    })),
  ];

  return (
    <>
      <FormSheet
        open={open}
        onOpenChange={onOpenChange}
        title={isEdit ? t('meetings.editTitle') : t('meetings.createTitle')}
        description={
          isEdit
            ? meeting.series
              ? `${meeting.title} · ${t('meetings.recurrence.seriesBadge')}: ${describeSeries(meeting.series, t, i18n.language)}`
              : (meeting.title ?? undefined)
            : t('meetings.createDesc')
        }
        onSubmit={onSubmit}
        busy={mutation.isPending || checking}
        // mentetlen változásnál a mellékattintás / Esc / X előbb rákérdez (a #20 óta a tartalom újranyitáskor
        // nem marad meg, tehát a figyelmeztetés nélküli bezárás adatvesztés lenne)
        dirty={form.formState.isDirty}
        footer={
          <>
            {isEdit && canDelete && onRequestDelete && (
              // Törlés a szerkesztő láb-gombjából (a naptáron nincs sor-akció) — balra tolva (`mr-auto`); a
              // megerősítést + a mutációt a szülő intézi (meetings.delete).
              <Button
                variant="destructive"
                type="button"
                className="mr-auto"
                onClick={() => onRequestDelete(meeting)}
                disabled={mutation.isPending || checking}
              >
                {t('meetings.delete')}
              </Button>
            )}
            <Button
              variant="outline"
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending || checking}
            >
              {t('meetings.cancel')}
            </Button>
            <Button type="submit" variant="brand" disabled={mutation.isPending || checking}>
              {mutation.isPending ? t('meetings.saving') : t('meetings.save')}
            </Button>
          </>
        }
      >
        <Form {...form}>
          <TextField<FormValues>
            name="title"
            label={t('meetings.fields.title')}
            required
            placeholder={t('meetings.fields.titlePlaceholder')}
          />
          <TextareaField<FormValues>
            name="description"
            label={t('meetings.fields.description')}
            rows={2}
          />
          <SelectField<FormValues>
            name="locationKey"
            label={t('meetings.fields.location')}
            required
            options={locationOptions}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Az idő-rács a megbeszélés-ablakra szűkül (06:00–22:00) — ugyanaz a két konstans, amit a naptár
              sávjai és a mentés-validáció is használ; a lista így nem ajánl fel hajnali időpontot. */}
            <DateTimeField<FormValues>
              name="startsAt"
              label={t('meetings.fields.startsAt')}
              minTime={BUSINESS_WINDOW.from}
              maxTime={BUSINESS_WINDOW.to}
              required
            />
            <DateTimeField<FormValues>
              name="endsAt"
              label={t('meetings.fields.endsAt')}
              minTime={BUSINESS_WINDOW.from}
              maxTime={BUSINESS_WINDOW.to}
              required
            />
          </div>
          {!isEdit && (
            <MeetingRecurrenceFields<FormValues>
              startsAtIso={localInputToIso(watch('startsAt'))}
              endsAtIso={localInputToIso(watch('endsAt'))}
              startLocal={watch('startsAt')}
            />
          )}
          {/* Online-linkek — a BFF (és a form) csak EGYET enged. */}
          <TextField<FormValues>
            name="zoomLink"
            label={t('meetings.fields.zoomLink')}
            placeholder="https://zoom.us/j/…"
          />
          <SelectField<FormValues>
            name="zoomAccountKey"
            label={t('meetings.fields.zoomAccount')}
            options={zoomAccountOptions}
          />
          <TextField<FormValues>
            name="googleMeetLink"
            label={t('meetings.fields.googleMeetLink')}
            placeholder="https://meet.google.com/…"
          />
          <TextField<FormValues>
            name="microsoftTeamsLink"
            label={t('meetings.fields.microsoftTeamsLink')}
            placeholder="https://teams.microsoft.com/…"
          />

          {/* Résztvevők — KÉT type-szűrt multiselect (munkatársak + kapcsolattartók); a BFF pivot-diffeli. */}
          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t('meetings.fields.employees')}</p>
            <EntityMultiSelect
              options={employeeOptions}
              value={watch('employeeIds')}
              onChange={(ids) => setValue('employeeIds', ids, { shouldDirty: true })}
              placeholder={t('meetings.fields.employeesPlaceholder')}
              loading={refsQ.isLoading}
              countLabel={(n) => t('meetings.fields.participantsCount', { count: n })}
              groups={employeeGroups.length > 0 ? employeeGroups : undefined}
              ungroupedLabel={t('meetings.fields.employeesUngrouped')}
              aria-label={t('meetings.fields.employees')}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t('meetings.fields.contacts')}</p>
            <EntityMultiSelect
              options={contactOptions}
              value={watch('contactIds')}
              onChange={(ids) => setValue('contactIds', ids, { shouldDirty: true })}
              placeholder={t('meetings.fields.contactsPlaceholder')}
              loading={refsQ.isLoading}
              countLabel={(n) => t('meetings.fields.participantsCount', { count: n })}
              aria-label={t('meetings.fields.contacts')}
            />
          </div>

          {/* Résztvevő-áttekintés + saját RSVP — CSAK szerkesztésnél (a create-nél még nincs mentett résztvevő). */}
        </Form>
      </FormSheet>

      {/* SOROZAT-alkalom mentése: a hatókör választása (csak ez / ez és a következők / mind). */}
      <ConfirmDialog
        open={scopeValues !== null}
        onOpenChange={(o) => {
          if (!o) setScopeValues(null);
        }}
        title={t('meetings.seriesScope.edit.title')}
        description={t('meetings.seriesScope.edit.desc')}
        confirmLabel={t('meetings.save')}
        cancelLabel={t('meetings.cancel')}
        onConfirm={() => {
          const values = scopeValues;
          setScopeValues(null);
          scopeRef.current = scopeChoice;
          if (values) void checkAndSave(values);
        }}
      >
        <MeetingSeriesScopeOptions value={scopeChoice} onChange={setScopeChoice} action="edit" />
      </ConfirmDialog>

      {/* RÉSZTVEVŐ-ÜTKÖZÉS megerősítése — átléphető figyelmeztetés (nem tiltás). */}
      <ConfirmDialog
        open={pendingValues !== null}
        onOpenChange={(o) => {
          if (!o) setPendingValues(null);
        }}
        tone="warning"
        title={t('meetings.participantConflict.title')}
        description={t('meetings.participantConflict.desc', { count: conflicts.length })}
        confirmLabel={t('meetings.participantConflict.proceed')}
        cancelLabel={t('meetings.participantConflict.back')}
        pendingLabel={t('meetings.saving')}
        pending={mutation.isPending}
        onConfirm={() => {
          const values = pendingValues;
          setPendingValues(null);
          if (values) doSubmit(values);
        }}
      >
        <MeetingParticipantConflictList conflicts={conflicts} />
      </ConfirmDialog>
    </>
  );
}
